import AltRouteIcon from '@mui/icons-material/AltRoute';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TableChartIcon from '@mui/icons-material/TableChart';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { jaJP } from '@mui/x-data-grid/locales';
import { DataGridPremium, GridPaginationModel, GridRowParams } from '@mui/x-data-grid-premium';
import { DatePicker } from '@mui/x-date-pickers';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import axios from 'axios';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SummaryDataResponse,
  useQueryBillingPerformanceKpi,
  useQueryBillingPerformanceOrderDetails,
  useQueryBillingPerformanceRouteDetails,
  useQueryBillingPerformanceSummaries,
} from 'src/hooks/useQueryBillingPerformances';

import { getSummaryColumns, orderDetailColumns, routeDetailColumns } from './Columns';
import { PeriodMode, tabLabels, tabTypes } from './constants';
import KPICardGrid from './KPICardGrid';

type GroupByType = 'shipper' | 'carrier' | 'truck' | 'driver';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const downloadCsvFile = (blob: Blob, contentDisposition: string | undefined, fallbackFilename: string) => {
  let filename = fallbackFilename;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    if (match?.[1]) {
      filename = decodeURIComponent(match[1].replace(/"/g, ''));
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const DRAWER_WIDTH = {
  width: '60vw',
  minWidth: 750,
  maxWidth: 1100,
};

const BillingPerformanceSection: FC = memo(() => {
  const [analysisTab, setAnalysisTab] = useState(0);
  const [periodMode, setPeriodMode] = useState<PeriodMode>('daily');
  const [detailViewMode, setDetailViewMode] = useState<'order' | 'route'>('order');
  const [searchKeyword] = useState('');
  const today = new Date();
  const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const [startDate, setStartDate] = useState<Date | null>(oneWeekAgo);
  const [endDate, setEndDate] = useState<Date | null>(today);
  const [selectedSummaryRow, setSelectedSummaryRow] = useState<SummaryDataResponse | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // DateRangePicker local state (日次モード用)
  const [selectedRange, setSelectedRange] = useState<DateRange<Date>>([startDate, endDate]);
  /** ピッカー内で選択中の範囲。onClose で startDate/endDate に反映するまでリクエストしない */
  const pendingRangeRef = useRef<DateRange<Date>>([startDate, endDate]);
  // 月次モード用
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(today);

  useEffect(() => {
    setSelectedRange([startDate, endDate]);
    pendingRangeRef.current = [startDate, endDate];
  }, [startDate, endDate]);

  // Export menu
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isExportMenuOpen = Boolean(exportMenuAnchorEl);
  const [isExporting, setIsExporting] = useState(false);

  // Pagination state
  const [summaryPaginationModel, setSummaryPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [detailPaginationModel, setDetailPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const groupBy = tabTypes[analysisTab] as GroupByType;

  // KPI Query
  const kpiQuery = useQueryBillingPerformanceKpi({
    startDate,
    endDate,
    periodMode,
    // TODO: locationFilter を location_id に変換
  });

  // Summaries Query
  const summariesQuery = useQueryBillingPerformanceSummaries({
    startDate,
    endDate,
    groupBy,
    keyword: searchKeyword || undefined,
    page: summaryPaginationModel.page + 1,
    perPage: summaryPaginationModel.pageSize,
    periodMode,
    // TODO: locationFilter を location_id に変換
  });

  // Detail Query params based on selected summary row
  const detailParams = useMemo(() => {
    const baseParams = {
      startDate,
      endDate,
      page: detailPaginationModel.page + 1,
      perPage: detailPaginationModel.pageSize,
      periodMode,
      // TODO: locationFilter を location_id に変換
    };

    if (!selectedSummaryRow) {
      return baseParams;
    }

    // 日次モードの場合は summary_date を追加
    const summaryDate = periodMode === 'daily' ? (selectedSummaryRow.date ?? undefined) : undefined;

    // shipper/carrier は name でフィルタ、truck/driver は entity_id でフィルタ
    if (groupBy === 'shipper' || groupBy === 'carrier') {
      return {
        ...baseParams,
        groupBy,
        entityName: selectedSummaryRow.name,
        summaryDate,
      };
    }
    return {
      ...baseParams,
      groupBy,
      entityId: selectedSummaryRow.entity_id ?? undefined,
      summaryDate,
    };
  }, [startDate, endDate, selectedSummaryRow, groupBy, detailPaginationModel, periodMode]);

  // Order Details Query (only when drawer is open)
  const orderDetailsQuery = useQueryBillingPerformanceOrderDetails(
    detailParams,
    isDrawerOpen && detailViewMode === 'order',
  );

  // Route Details Query (only when drawer is open)
  const routeDetailsQuery = useQueryBillingPerformanceRouteDetails(
    detailParams,
    isDrawerOpen && detailViewMode === 'route',
  );

  const handleAnalysisTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setAnalysisTab(newValue);
    setSelectedSummaryRow(null);
    setIsDrawerOpen(false);
    setSummaryPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handlePeriodModeChange = useCallback((
    _event: React.MouseEvent<HTMLElement>,
    newMode: PeriodMode | null,
  ) => {
    if (newMode !== null) {
      setPeriodMode(newMode);
      setSelectedSummaryRow(null);
      setIsDrawerOpen(false);
      setSummaryPaginationModel((prev) => ({ ...prev, page: 0 }));

      if (newMode === 'monthly') {
        // 月次に切替: 現在の startDate の月を設定し、その月全体を期間に設定
        const base = startDate ?? new Date();
        setSelectedMonth(base);
        setStartDate(startOfMonth(base));
        setEndDate(endOfMonth(base));
      }
    }
  }, [startDate]);

  const handleDateRangeChange = useCallback((range: DateRange<Date>) => {
    setSelectedRange((prev) => [range[0] ?? prev[0], range[1] ?? prev[1]]);
    // ピッカーから渡る生の範囲のみ保持。開始・終了の両方が選ばれたときだけ onClose で確定する
    pendingRangeRef.current = [range[0] ?? null, range[1] ?? null];
  }, []);

  const handleDateRangePickerClose = useCallback(() => {
    const range = pendingRangeRef.current;
    const [start, end] = range ?? [null, null];
    const isValidRange = start && end && start.getTime() <= end.getTime();

    if (isValidRange) {
      setStartDate(start);
      setEndDate(end);
      setSummaryPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDetailPaginationModel((prev) => ({ ...prev, page: 0 }));
      setSelectedSummaryRow(null);
      setIsDrawerOpen(false);
    } else {
      // 開始 > 終了など不正な範囲の場合は元の値に戻す
      const reverted: DateRange<Date> = [startDate, endDate];
      setSelectedRange(reverted);
      pendingRangeRef.current = reverted;
    }
  }, [startDate, endDate]);

  const handleMonthChange = useCallback((date: Date | null) => {
    if (date) {
      setSelectedMonth(date);
      setStartDate(startOfMonth(date));
      setEndDate(endOfMonth(date));
      setSummaryPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDetailPaginationModel((prev) => ({ ...prev, page: 0 }));
      setSelectedSummaryRow(null);
      setIsDrawerOpen(false);
    }
  }, []);

  const handleViewModeChange = useCallback((
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'order' | 'route' | null
  ) => {
    if (newMode !== null) {
      setDetailViewMode(newMode);
      setDetailPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }, []);

  const handleSummaryRowClick = useCallback((params: GridRowParams<SummaryDataResponse>) => {
    // 同じ行をクリックしたらDrawerを閉じる
    if (selectedSummaryRow?.id === params.row.id) {
      setSelectedSummaryRow(null);
      setIsDrawerOpen(false);
    } else {
      setSelectedSummaryRow(params.row);
      setIsDrawerOpen(true);
      setDetailPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }, [selectedSummaryRow]);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedSummaryRow(null);
  }, []);

  const handleSummaryPaginationModelChange = useCallback((model: GridPaginationModel) => {
    setSummaryPaginationModel(model);
  }, []);

  const handleDetailPaginationModelChange = useCallback((model: GridPaginationModel) => {
    setDetailPaginationModel(model);
  }, []);

  // Export menu handlers
  const handleExportMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchorEl(event.currentTarget);
  }, []);

  const handleExportMenuClose = useCallback(() => {
    setExportMenuAnchorEl(null);
  }, []);

  const handleExport = useCallback(async (endpoint: string, filenamePrefix: string) => {
    handleExportMenuClose();
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.set('end_date', format(endDate, 'yyyy-MM-dd'));
      params.set('period_mode', periodMode);
      params.set('group_by', groupBy);
      if (searchKeyword) params.set('keyword', searchKeyword);

      const response = await axios.get(
        `/api/v5/billing_performances/${endpoint}/export?${params.toString()}`,
        { responseType: 'blob' },
      );

      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      const fallbackFilename = `${filenamePrefix}_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
      downloadCsvFile(response.data as Blob, contentDisposition, fallbackFilename);
    } catch {
      // エラー時は何もしない（ネットワークエラー等はaxiosインターセプターで処理）
    } finally {
      setIsExporting(false);
    }
  }, [handleExportMenuClose, startDate, endDate, periodMode, groupBy, searchKeyword]);

  const handleExportSummary = useCallback(() => {
    void handleExport('summaries', '集計データ');
  }, [handleExport]);

  const handleExportOrderDetails = useCallback(() => {
    void handleExport('order_details', '詳細データ_案件単位');
  }, [handleExport]);

  const handleExportRouteDetails = useCallback(() => {
    void handleExport('route_details', '詳細データ_ルート単位');
  }, [handleExport]);

  const summaryColumns = useMemo(
    () => getSummaryColumns(analysisTab, periodMode),
    [analysisTab, periodMode]
  );

  const summaryData = summariesQuery.data?.data ?? [];
  const summaryRowCount = summariesQuery.data?.meta?.total_count ?? 0;
  // 明細データのidをインデックスで上書き（APIのidが重複する場合があるため）
  const orderDetailData = useMemo(
    () => (orderDetailsQuery.data?.data ?? []).map((row, index) => ({ ...row, id: index })),
    [orderDetailsQuery.data],
  );
  const orderDetailRowCount = orderDetailsQuery.data?.meta?.total_count ?? 0;
  const routeDetailData = useMemo(
    () => (routeDetailsQuery.data?.data ?? []).map((row, index) => ({ ...row, id: index })),
    [routeDetailsQuery.data],
  );
  const routeDetailRowCount = routeDetailsQuery.data?.meta?.total_count ?? 0;

  const isDetailLoading = detailViewMode === 'order'
    ? orderDetailsQuery.isLoading || orderDetailsQuery.isFetching
    : routeDetailsQuery.isLoading || routeDetailsQuery.isFetching;

  // Drawer タイトル生成（name/date が null の場合は空文字で表示）
  const drawerTitle = useMemo(() => {
    if (!selectedSummaryRow) return '明細データ';
    const name = selectedSummaryRow.name ?? '';
    if (periodMode === 'daily' && selectedSummaryRow.date) {
      const date = selectedSummaryRow.date ?? '';
      return `${date} - ${name} 詳細`;
    }
    return `${name} 詳細`;
  }, [selectedSummaryRow, periodMode]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* KPIバー */}
      <KPICardGrid kpis={kpiQuery.data} isLoading={kpiQuery.isLoading} />

      {/* 条件行: 日次/月次 + 日付範囲 + エクスポート */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 1.5,
        mb: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={periodMode}
            exclusive
            onChange={handlePeriodModeChange}
            size="small"
          >
            <ToggleButton value="daily">日次</ToggleButton>
            <ToggleButton value="monthly">月次</ToggleButton>
          </ToggleButtonGroup>
          {periodMode === 'daily' ? (
            <DateRangePicker
              value={selectedRange}
              onChange={handleDateRangeChange}
              onClose={handleDateRangePickerClose}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { backgroundColor: 'background.paper' },
                },
              }}
            />
          ) : (
            <DatePicker
              value={selectedMonth}
              onChange={handleMonthChange}
              views={['year', 'month']}
              openTo="month"
              format="yyyy年MM月"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { backgroundColor: 'background.paper', width: 180 },
                },
              }}
            />
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={handleExportMenuOpen}
          disabled={isExporting}
        >
          エクスポート
        </Button>
        <Menu
          anchorEl={exportMenuAnchorEl}
          open={isExportMenuOpen}
          onClose={handleExportMenuClose}
          slotProps={{ list: { sx: { p: 0, m: 0 } } }}
        >
          <MenuItem onClick={handleExportSummary}>
            <ListItemIcon>
              <TableChartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>集計データ</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportOrderDetails}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>詳細データ（案件単位）</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportRouteDetails}>
            <ListItemIcon>
              <AltRouteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>詳細データ（ルート単位）</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* 分析タブ */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={analysisTab}
          onChange={handleAnalysisTabChange}
          sx={{ px: 3 }}
        >
          {tabLabels.map((label, index) => (
            <Tab key={index} label={label} />
          ))}
        </Tabs>
      </Box>

      {/* 集計データグリッド（残り領域でスクロール） */}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {(summariesQuery.isLoading || summariesQuery.isFetching) && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <DataGridPremium
          rows={summaryData}
          columns={summaryColumns}
          density="compact"
          disableRowSelectionOnClick={false}
          onRowClick={handleSummaryRowClick}
          localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
          getRowId={(row) => row.id}
          rowSelectionModel={selectedSummaryRow ? { type: 'include', ids: new Set([selectedSummaryRow.id]) } : { type: 'include', ids: new Set() }}
          pagination
          paginationMode="server"
          rowCount={summaryRowCount}
          paginationModel={summaryPaginationModel}
          onPaginationModelChange={handleSummaryPaginationModelChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          sx={{
            height: '100%',
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'primary.light',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      </Box>

      {/* 明細 Drawer */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        variant="temporary"
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'transparent' },
          },
        }}
        PaperProps={{
          sx: {
            ...DRAWER_WIDTH,
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {/* Drawer ヘッダー */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
        }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {drawerTitle}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup
              value={detailViewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="order">案件単位</ToggleButton>
              <ToggleButton value="route">ルート単位</ToggleButton>
            </ToggleButtonGroup>
            <IconButton onClick={handleDrawerClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Drawer コンテンツ */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', p: 2 }}>
          {isDetailLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {detailViewMode === 'order' ? (
            <DataGridPremium
              rows={orderDetailData}
              columns={orderDetailColumns}
              density="compact"
              disableRowSelectionOnClick
              localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
              getRowId={(row) => row.id}
              pagination
              paginationMode="server"
              rowCount={orderDetailRowCount}
              paginationModel={detailPaginationModel}
              onPaginationModelChange={handleDetailPaginationModelChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                height: "100%",
                "& .MuiDataGrid-columnHeaderTitle": {
                  whiteSpace: "nowrap",
                  overflow: "visible",
                },
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          ) : (
            <DataGridPremium
              rows={routeDetailData}
              columns={routeDetailColumns}
              density="compact"
              disableRowSelectionOnClick
              localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
              getRowId={(row) => row.id}
              pagination
              paginationMode="server"
              rowCount={routeDetailRowCount}
              paginationModel={detailPaginationModel}
              onPaginationModelChange={handleDetailPaginationModelChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                height: "100%",
                "& .MuiDataGrid-columnHeaderTitle": {
                  whiteSpace: "nowrap",
                  overflow: "visible",
                },
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
});

export default BillingPerformanceSection;
